/**
 * Public Feedback Form
 * ====================
 * 
 * Customer-facing feedback form for transactions.
 * Accessed via QR code on receipts: /feedback/{merchant-slug}/{transaction-id}
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Star,
  Send,
  Check,
  MessageSquare,
  Loader2,
  Home,
  AlertCircle,
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const FeedbackPage = () => {
  const { merchantSlug, transactionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [merchantName, setMerchantName] = useState("");
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkFeedback = async () => {
      try {
        const res = await fetch(`${API}/feedback/check/${merchantSlug}/${transactionId}`);
        if (res.ok) {
          const data = await res.json();
          setMerchantName(data.merchant_name);
          setAlreadySubmitted(data.already_submitted);
        } else if (res.status === 404) {
          setError("Invalid feedback link");
        }
      } catch (err) {
        console.error("Error checking feedback:", err);
        setError("Unable to load feedback form");
      } finally {
        setLoading(false);
      }
    };
    
    checkFeedback();
  }, [merchantSlug, transactionId]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API}/feedback/submit/${merchantSlug}/${transactionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating,
          comment: comment.trim() || null
        })
      });
      
      if (res.ok) {
        setSubmitted(true);
        toast.success("Thank you for your feedback!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit feedback");
      }
    } catch (err) {
      console.error("Error submitting feedback:", err);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center text-gray-900">
        <div className="text-center p-8">
          <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Oops!</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white hover:opacity-90">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center text-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Already Submitted</h1>
          <p className="text-gray-500 mb-6">
            You've already shared feedback for this purchase. Thank you!
          </p>
          <Link to="/">
            <Button className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white hover:opacity-90">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center text-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Thank You!</h1>
          <p className="text-gray-500 mb-6">
            Your feedback has been submitted. We appreciate you taking the time to share your experience with {merchantName}.
          </p>
          <Link to="/">
            <Button className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white hover:opacity-90">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header - Brand Gradient */}
      <header className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-center">
          <img src={COMPANY_LOGO} alt="Veyronix" className="h-8" />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-gray-900">How was your experience?</h1>
          <p className="text-gray-500">
            Share your feedback for <span className="text-gray-900 font-medium">{merchantName}</span>
          </p>
        </div>

        {/* Rating */}
        <div className="mb-8">
          <p className="text-sm text-gray-500 text-center mb-4">Tap to rate</p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="p-2 transition-transform hover:scale-110"
                data-testid={`rating-star-${star}`}
              >
                <Star
                  className={`h-10 w-10 transition-colors ${
                    star <= (hoveredRating || rating)
                      ? "fill-amber-400 text-amber-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center mt-2 text-amber-500 font-medium">
              {rating === 5 && "Excellent!"}
              {rating === 4 && "Great!"}
              {rating === 3 && "Good"}
              {rating === 2 && "Fair"}
              {rating === 1 && "Poor"}
            </p>
          )}
        </div>

        {/* Comment */}
        <div className="mb-8">
          <label className="text-sm text-gray-600 block mb-2">Additional comments (optional)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more about your experience..."
            className="w-full h-32 p-4 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            data-testid="feedback-comment"
          />
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full h-14 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:opacity-90 text-white text-lg"
          data-testid="submit-feedback-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Your feedback helps us improve our service
        </p>
      </div>
    </div>
  );
};

export default FeedbackPage;
